import React                      from 'react';
import { observer }               from 'mobx-react-lite';
import View                       from '@widesk-ui/components/View';
import { message }                from '@widesk-ui/hooks/useMessage';
import ApiCollection              from '@widesk-core/models/ApiCollection';
import PlaceTypeModel             from '@models/map/PlaceTypeModel';
import SelectPlaceType            from '@selects/map/SelectPlaceType';
import Field                      from '@/TokamapEditor/ux/AreaSidebar/Field';
import currentProjectEditorStore  from '@/TokamapEditor/stores/currentProjectEditorStore';
import useDesignTokens            from '@/hooks/useDesignTokens';
import { Theme }                  from '@/TokamapEditor/stores/themeStore';
import { proposeApplyTheme }      from '@/TokamapEditor/helpers/placeTypeThemeShapes';
import { proposeDetachTheme }     from '@/TokamapEditor/helpers/placeTypeThemeShapes';

/** Le lien est porté par le type (`PlaceType.theme`) : un `PATCH` par type ajouté ou retiré. */
const ThemePlaceTypesField = observer(({ theme }: { theme: Theme; }) => {
	const tokens = useDesignTokens();
	const project = currentProjectEditorStore.get.project;
	const [placeTypeIds, setPlaceTypeIds] = React.useState<id[]>([]);
	const [loading, setLoading] = React.useState(true);

	React.useEffect(() => {
		let ignore = false;

		setLoading(true);

		void new ApiCollection(PlaceTypeModel)
			.setRequiredFilter('project', project.id)
			.setFilter('theme', theme.id)
			.list()
			.then(collection => {
				if (ignore) return;

				setPlaceTypeIds(collection.map(placeType => String(placeType.id)));
				setLoading(false);
			})
			.catch(() => !ignore && setLoading(false));

		return () => {
			ignore = true;
		};
	}, [theme.id, project.id]);

	const commit = async (next: id[]) => {
		const added = next.filter(id => !placeTypeIds.includes(id));
		const removed = placeTypeIds.filter(id => !next.includes(id));

		if (!added.length && !removed.length) return;

		const previous = placeTypeIds;

		setPlaceTypeIds(next);

		try {
			await Promise.all([
				...added.map(id => new PlaceTypeModel({ id }).patch({ theme: `$:map:theme:${theme.id}` })),
				// `null` explicite : en merge-patch, une clé absente laisserait le lien en place.
				...removed.map(id => new PlaceTypeModel({ id }).patch({ theme: null })),
			]);
		} catch (err) {
			setPlaceTypeIds(previous);

			return message.exception(err);
		}

		if (added.length) await proposeApplyTheme(theme, added, project.id);
		if (removed.length) await proposeDetachTheme(theme, removed, project.id);
	};

	return (
		<Field label={PlaceTypeModel.localize('plural')}>
			<View gap="xs">
				<SelectPlaceType
					identifier="id"
					multiple
					disabled={loading}
					placeholder={`Aucun ${PlaceTypeModel.localize('default', 'l')}`}
					projectUrn={project.urn}
					value={placeTypeIds}
					onChange={value => void commit((value || []) as id[])}
				/>

				<View color={tokens.text.muted} size={12.5}>
					Les points d'intérêts de ces types reçoivent ce thème.
				</View>
			</View>
		</Field>
	);
});

export default ThemePlaceTypesField;
